import { useState, useEffect } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, Calendar, Download } from "lucide-react";
import { api } from "../api/client";
import type { AttendanceLog } from "../types";

export default function AttendanceLog() {
  const [logs, setLogs] = useState<AttendanceLog[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const limit = 30;

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    setIsLoading(true);
    try {
      const data = await api.getAttendanceLogs({ page, limit });
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Fetch all logs (limit 1000 for export)
      const data = await api.getAttendanceLogs({ page: 1, limit: 1000 });
      
      // Create CSV content
      const headers = ["Bil", "Nama", "No. IC", "Institusi", "Slot Masa", "Umur", "DUN", "Tarikh Daftar", "Masa Daftar", "Jenis"];
      const rows = data.logs.map((log: any) => [
        log.bil || "",
        log.nama || "",
        log.noIc || "",
        log.institution || "",
        log.slotMasa || "",
        log.age ?? "",
        log.dun || "",
        log.checkedInAt ? new Date(log.checkedInAt).toLocaleDateString("ms-MY") : "",
        log.checkedInAt ? new Date(log.checkedInAt).toLocaleTimeString("ms-MY") : "",
        log.wakilNama ? `WAKIL - ${log.wakilNama} (${log.wakilHubungan})` : "SENDIRI",
      ]);

      const csvContent = [
        headers.join(","),
        ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      ].join("\n");

      // Download file
      const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `log_kehadiran_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export error:", err);
    }
  };

  const totalPages = Math.ceil(total / limit);

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ms-MY", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString("ms-MY", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Log Kehadiran</h1>
          <p className="text-slate-600">Rekod semua daftar masuk penerima</p>
        </div>
        <button
          onClick={handleExportCSV}
          disabled={logs.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition"
        >
          <Download className="w-4 h-4" />
          Export CSV
        </button>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12 text-slate-500">
            Tiada log kehadiran lagi
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Bil</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Nama</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">No. IC</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Institusi</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Slot Masa</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Umur</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">DUN</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Tarikh</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Masa</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Oleh</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((log) => (
                    <tr key={log.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-mono text-sm">{log.bil || "-"}</td>
                      <td className="py-3 px-4">{log.nama || "-"}</td>
                      <td className="py-3 px-4 font-mono text-sm">{log.noIc || "-"}</td>
                      <td className="py-3 px-4 text-sm">{log.institution || "-"}</td>
                      <td className="py-3 px-4 text-sm">{log.slotMasa || "-"}</td>
                      <td className="py-3 px-4 text-sm">{log.age ?? "-"}</td>
                      <td className="py-3 px-4 text-sm">{log.dun || "-"}</td>
                      <td className="py-3 px-4 text-sm flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-slate-400" />
                        {formatDate(log.checkedInAt || "")}
                      </td>
                      <td className="py-3 px-4 text-sm font-mono">{formatTime(log.checkedInAt || "")}</td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {log.wakilNama ? `WAKIL: ${log.wakilNama}` : "SENDIRI"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200">
                <p className="text-sm text-slate-500">
                  Showing {(page - 1) * limit + 1} to {Math.min(page * limit, total)} of {total}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 flex items-center gap-1"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="px-3 py-1 text-sm">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 border border-slate-300 rounded text-sm disabled:opacity-50 flex items-center gap-1"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
